export interface CmsPaginationInfoContract {
    limit: number;
    nextOffset: number;
    startOffset: number;
    total: number;
}

export interface CmsMetaKeyOptionValueContract {
    referenceId: string;
    value?: string;
}

export function ctaTypeCmsMetaKeyOptionValueValueOf(value: string): keyof typeof CtaTypeCmsMetaKeyOptionValueOption {
    return Object.entries(CtaTypeCmsMetaKeyOptionValueOption).find(([_k, v]) => v === value)?.[0] as keyof typeof CtaTypeCmsMetaKeyOptionValueOption;
}

export enum CtaTypeCmsMetaKeyOptionValueOption {
    none = 'none',
    button = 'mk_cta_type_button',
    contact = 'mk_cta_type_contact',
    download = 'mk_cta_type_download',
    link = 'mk_cta_type_link',
}


export interface CtaTypeCmsMetaKeyOptionValueContract extends CmsMetaKeyOptionValueContract {
    referenceId: CtaTypeCmsMetaKeyOptionValueOption;
}

export function eventTypeCmsMetaKeyOptionValueValueOf(value: string): keyof typeof EventTypeCmsMetaKeyOptionValueOption {
    return Object.entries(EventTypeCmsMetaKeyOptionValueOption).find(([_k, v]) => v === value)?.[0] as keyof typeof EventTypeCmsMetaKeyOptionValueOption;
}


export enum EventTypeCmsMetaKeyOptionValueOption {
    digital = 'mk_event_type_digital',
    hybrid = 'mk_event_type_hybrid',
    live = 'mk_event_type_live',
    webinar = 'mk_event_type_webinar',
    physical = 'mk_event_type_physical',
}


export function officeTypeCmsMetaKeyOptionValueValueOf(value: string): keyof typeof OfficeTypeCmsMetaKeyOptionValueOption {
    return Object.entries(OfficeTypeCmsMetaKeyOptionValueOption).find(([_k, v]) => v === value)?.[0] as keyof typeof OfficeTypeCmsMetaKeyOptionValueOption;
}

export enum OfficeTypeCmsMetaKeyOptionValueOption {
    administration = 'mk_office_office_type_administration',
    headquarters = 'mk_office_office_type_headquarters',
    operating = 'mk_office_office_type_operating',
    registered = 'mk_office_office_type_registered',
}

export interface OfficeTypeCmsMetaKeyOptionValueContract extends CmsMetaKeyOptionValueContract {
    referenceId: OfficeTypeCmsMetaKeyOptionValueOption;
}

export interface AltDefaultBackgroundCmsMetaKeyOptionValueContract extends CmsMetaKeyOptionValueContract {
    referenceId: AltDefaultBackgroundCmsMetaKeyOptionValueOption;
}

export function altDefaultBackgroundCmsMetaKeyOptionValueValueOf(value: string): keyof typeof AltDefaultBackgroundCmsMetaKeyOptionValueOption {
    return Object.entries(AltDefaultBackgroundCmsMetaKeyOptionValueOption).find(([_k, v]) => v === value)?.[0] as keyof typeof AltDefaultBackgroundCmsMetaKeyOptionValueOption;
}

export enum AltDefaultBackgroundCmsMetaKeyOptionValueOption {
    bg01 = 'mk_alt_background_bg01',
    bg02 = 'mk_alt_background_bg02',
    bg03 = 'mk_alt_background_bg03',
    bg04 = 'mk_alt_background_bg04',
    bg05 = 'mk_alt_background_bg05',
}

export interface ContentTypeCmsMetaKeyOptionValueContract extends CmsMetaKeyOptionValueContract {
    referenceId: ContentTypeCmsMetaKeyOptionValueOption;
}

export function contentTypeCmsMetaKeyOptionValueValueOf(value: string): keyof typeof ContentTypeCmsMetaKeyOptionValueOption {
    return Object.entries(ContentTypeCmsMetaKeyOptionValueOption).find(([_k, v]) => v === value)?.[0] as keyof typeof ContentTypeCmsMetaKeyOptionValueOption;
}

export enum ContentTypeCmsMetaKeyOptionValueOption {
    externalPage = 'mk_content_type_externalpage',
    landingPage = 'mk_content_type_landingpage',
    overlay = 'mk_content_type_overlay',
    pdf = 'mk_content_type_pdf',
}

export enum WebsiteMainSiteTypeCmsMetaKeyOptionValueOption {
    br = 'mk_website_main_sites_br',
    ch = 'mk_website_main_sites_ch',
    de = 'mk_website_main_sites_de',
    fr = 'mk_website_main_sites_fr',
    it = 'mk_website_main_sites_it',
    uk = 'mk_website_main_sites_uk',
}

export interface EventTypeCmsMetaKeyOptionValueContract extends CmsMetaKeyOptionValueContract {
    referenceId: EventTypeCmsMetaKeyOptionValueOption;
}

export interface CmsMetaOptionValueContract {
    id?: number;
    optionValue: CmsMetaKeyOptionValueContract;
    order?: number;
}

export interface CmsMetaContract {
    key: CmsMetaKey;
    value?: string;
    optionValues?: CmsMetaOptionValueContract[];
}

export type CmsMetaKey =
    | 'alt_background'
    | 'content_type'
    | 'cover_media'
    | 'card_logo'
    | 'card_video'
    | 'card_type'
    | 'cta_label'
    | 'cta_type'
    | 'cta_url'
    | 'document'
    | 'event_type'
    | 'event_location'
    | 'event_language'
    | 'office_latitude'
    | 'office_longitude'
    | 'office_region'
    | 'office_country'
    | 'office_city'
    | 'office_main_office'
    | 'office_address_line_1'
    | 'office_address_line_2'
    | 'office_phone'
    | 'office_fax'
    | 'office_office_type'
    | 'office_google_maps_url'
    | 'website_info_label'
    | 'website_main_sites'
    | 'website_site_links'
    | 'industries'
    | 'services'
    | 'attribution_company'
    | 'attribution_country'
    | 'business_impact'
;

export function cmsMetaExtractorAnyValue(content: CmsContentContract, key: CmsMetaKey): string {
    const meta: CmsMetaContract = content.meta?.find((meta: CmsMetaContract): boolean => meta.key === key);
    if (!meta) {
        return undefined;
    }
    if (meta.optionValues?.length) {
        return meta.optionValues[0]?.optionValue.referenceId;
    }
    return meta.value;
}

export function cmsMetaExtractorAnyOption<E>(content: CmsContentContract, key: CmsMetaKey): E {
    const meta: CmsMetaContract = content.meta?.find((meta: CmsMetaContract): boolean => meta.key === key);
    if (!meta) {
        return undefined;
    }
    if (meta.optionValues?.length) {
        return (<unknown> meta.optionValues[0]?.optionValue.referenceId) as E;
    }
    return undefined;
}

export function cmsMetaExtractorValues(content: CmsContentContract, key: CmsMetaKey, returnValues: boolean = false): string[] {
    const meta: CmsMetaContract = content.meta?.find((meta: CmsMetaContract): boolean => meta.key === key);
    if (!meta) {
        return undefined;
    }
    if (meta.optionValues?.length) {
        return [...meta.optionValues].sort((a: CmsMetaOptionValueContract, b: CmsMetaOptionValueContract): number => a.order - b.order)
            .map((wrapper: CmsMetaOptionValueContract) => returnValues ? wrapper.optionValue.value : wrapper.optionValue.referenceId);
    }
    return [meta.value];
}

export interface CmsPaperbitsPageRefContract {
    key: string;
    permalink: string;
    siteGroup: string;
}

export interface CmsGroupContract {
    id: number;
    externalId: string;
    name: string;
    shareMode: 'READ' | 'WRITE';
}

export interface CmsLanguageContract {
    code: string;
    language?: string;
}

export interface CMSContentRefContract {
    id: number;
    type: string;
    title: string;
    permalink: string;
}

export interface CmsContentContract {
    id: number;

    externalId?: string;
    closingDate?: string;
    eventDate?: string;
    eventTimezone?: string;
    language?: CmsLanguageContract;
    meta: CmsMetaContract[];
    paperbitsPageRef?: CmsPaperbitsPageRefContract;
    permalink?: string;
    publicationDate: string;
    publicationDateObj?: {time: number};
    sectionPermalink?: string;
    shortDescription?: string;
    title: string;
    type: string;
    url?: string;
    tags: string[];
    canonical?: CMSContentRefContract;
    groups?: CmsGroupContract[];
    status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED' | 'DELETED';
    section?: number;
}

export interface CmsContentsListContract {
    list: CmsContentContract[];
    paginationInfo: CmsPaginationInfoContract;
}
